// eslint-disable-next-line no-restricted-imports
import React, {
  FC,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {DevTools} from './devTools/DevTools';
import {AsyncData, SuccessResult} from './types';
// eslint-disable-next-line no-restricted-imports
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {ServerAwaitMetadataProvider} from './ServerAwaitMetadataProvider';

export type InitialData = Map<string, object>;

type DataFetcherProviderProps = {
  initialData?: Map<string, AsyncData>;
  disableDevTools?: boolean;
  queryClient?: QueryClient;
};

type DataFetcherContext = DataFetcherProviderProps & {
  deleteKey: (key: string) => void;
};

const DataFetcherContext = createContext<DataFetcherContext>({
  initialData: new Map(),
  deleteKey: (): void => {
    throw new Error(
      'The default action should never be used, replace it using DataFetcherProvider',
    );
  },
});

export const DataFetcherProvider: FC<
  React.PropsWithChildren<DataFetcherProviderProps>
> = ({queryClient, initialData, disableDevTools, children}) => {
  const deleteKey = useCallback(
    (key: string) => {
      initialData?.delete(key);
    },
    [initialData],
  );

  const value = useMemo(
    () => ({
      initialData,
      deleteKey,
    }),
    [initialData, deleteKey],
  );

  return (
    <DataFetcherContext.Provider value={value as DataFetcherContext}>
      <QueryClientProviderUniversal queryClient={queryClient}>
        {children}

        <DevTools disableDevTools={disableDevTools} />
      </QueryClientProviderUniversal>
    </DataFetcherContext.Provider>
  );
};

export const useDataFetcherInitialData = <T extends object = object>(
  key: string,
): SuccessResult<T> | undefined => {
  const context = useContext(DataFetcherContext);

  return context.initialData?.get(key) as SuccessResult<T>;
};

export const useDataFetcherAction = () => {
  const {deleteKey} = useContext(DataFetcherContext);

  return {deleteKey};
};

const QueryClientProviderUniversal: FC<
  React.PropsWithChildren<{queryClient?: QueryClient}>
> = ({children, queryClient}) => {
  const [client] = useState(() => queryClient ?? new QueryClient({}));

  const AwaitMetadataProvider = process.browser
    ? React.Fragment
    : ServerAwaitMetadataProvider;

  return (
    <QueryClientProvider client={client}>
      <AwaitMetadataProvider>{children}</AwaitMetadataProvider>
    </QueryClientProvider>
  );
};
